"use client";
"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMergeStyles = exports.useInheritedStylesContext = exports.InheritedStylesProvider = void 0;
const react_1 = __importDefault(require("react"));
const InheritedStylesContext = react_1.default.createContext({
    color: "text01",
    fontFamily: "body",
    fontSize: 2,
    overflowWrap: "break-word"
});
const InheritedStylesProvider = ({ children, styleContext }) => {
    return (react_1.default.createElement(InheritedStylesContext.Provider, { value: styleContext }, children));
};
exports.InheritedStylesProvider = InheritedStylesProvider;
const useInheritedStylesContext = () => react_1.default.useContext(InheritedStylesContext);
exports.useInheritedStylesContext = useInheritedStylesContext;
/**
 * Hook to merge styles from sx object and inheritedStylesContext
 *
 * @param {BoxSx} sx - The styles to merge with the context styles.
 * @returns {MergeStylesResult} {newInheritedStylesContext: InheritedStylesContext, mergedSx: BoxSx}
 * @example
 *
 * const ExampleComponent = ({children, sx}) => {
 *  const { newInheritedStylesContext, mergedSx } = useMergeStyles(sx);
 *  return (
 *    <InheritedStylesProvider styleContext={newInheritedStylesContext}>
 *      <Box sx={mergedSx}>
 *       {children}
 *      </Box>
 *    </InheritedStylesProvider>
 *  )
 * }
 *
 */
const useMergeStyles = (sx) => {
    const currentInheritedStylesContext = react_1.default.useContext(InheritedStylesContext);
    const newInheritedStylesContext = react_1.default.useMemo(() => {
        return {
            color: (sx === null || sx === void 0 ? void 0 : sx.color) || currentInheritedStylesContext.color,
            fontFamily: (sx === null || sx === void 0 ? void 0 : sx.fontFamily) || currentInheritedStylesContext.fontFamily,
            fontSize: (sx === null || sx === void 0 ? void 0 : sx.fontSize) || currentInheritedStylesContext.fontSize,
            fontWeight: (sx === null || sx === void 0 ? void 0 : sx.fontWeight) || currentInheritedStylesContext.fontWeight,
            lineHeight: (sx === null || sx === void 0 ? void 0 : sx.lineHeight) || currentInheritedStylesContext.lineHeight,
            letterSpacing: (sx === null || sx === void 0 ? void 0 : sx.letterSpacing) || currentInheritedStylesContext.letterSpacing,
            overflowWrap: (sx === null || sx === void 0 ? void 0 : sx.overflowWrap) || currentInheritedStylesContext.overflowWrap,
            textRendering: (sx === null || sx === void 0 ? void 0 : sx.textRendering) || currentInheritedStylesContext.textRendering
        };
    }, [
        currentInheritedStylesContext.color,
        currentInheritedStylesContext.fontFamily,
        currentInheritedStylesContext.fontSize,
        currentInheritedStylesContext.fontWeight,
        currentInheritedStylesContext.letterSpacing,
        currentInheritedStylesContext.lineHeight,
        currentInheritedStylesContext.overflowWrap,
        currentInheritedStylesContext.textRendering,
        sx === null || sx === void 0 ? void 0 : sx.color,
        sx === null || sx === void 0 ? void 0 : sx.fontFamily,
        sx === null || sx === void 0 ? void 0 : sx.fontSize,
        sx === null || sx === void 0 ? void 0 : sx.fontWeight,
        sx === null || sx === void 0 ? void 0 : sx.letterSpacing,
        sx === null || sx === void 0 ? void 0 : sx.lineHeight,
        sx === null || sx === void 0 ? void 0 : sx.overflowWrap,
        sx === null || sx === void 0 ? void 0 : sx.textRendering
    ]);
    // remove any inheritedProperties on the sx object that are explicly set to null or undeinfed - I.e. {fontFamily: undefined} - This will avoid breaking the style cascade
    cleanSx(sx);
    const mergedSx = Object.assign(Object.assign({}, newInheritedStylesContext), sx);
    return {
        newInheritedStylesContext,
        mergedSx
    };
};
exports.useMergeStyles = useMergeStyles;
const cleanSx = (sx) => {
    inheritedProperties.forEach((inheritedProperty) => {
        if (sx.hasOwnProperty(inheritedProperty) &&
            (sx[inheritedProperty] === undefined || sx[inheritedProperty] === null)) {
            delete sx[inheritedProperty];
        }
    });
};
const inheritedProperties = [
    "color",
    "fontFamily",
    "fontSize",
    "fontWeight",
    "lineHeight",
    "letterSpacing",
    "textRendering",
    "overflowWrap"
];
//# sourceMappingURL=useInheritedStyles.js.map